import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Wrench, 
  Droplets, 
  ThermometerSun, 
  Sparkles,
  CheckCircle,
  ArrowRight,
  Phone
} from 'lucide-react';

const HERO_BG = "https://images.unsplash.com/photo-1771218830084-fdd272e149a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBzdW5zZXQlMjBtZWRpdGVycmFuZWFuJTIwdmlsbGElMjBvY2VhbiUyMHZpZXd8ZW58MHx8fHwxNzczMzE4ODI3fDA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    id: 'construccion',
    icon: Building2,
    title: 'Construcción de Piscinas',
    subtitle: 'Tu piscina soñada, hecha realidad',
    description: 'Diseñamos y construimos piscinas a medida. Desde tradicionales hasta infinity pools.',
    features: ['Diseño 3D', 'Hormigón proyectado', 'Infinity pools', 'Iluminación LED'],
    image: 'https://images.unsplash.com/photo-1730135875620-795bca5badc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBtb2Rlcm4lMjB2aWxsYSUyMGJyaWdodCUyMGRheWxpZ2h0fGVufDB8fHx8MTc3MzMxNDg4Nnww&ixlib=rb-4.1.0&q=85',
    color: 'from-sky-500 to-blue-600',
    bgColor: 'bg-sky-50',
  },
  {
    id: 'mantenimiento',
    icon: Sparkles,
    title: 'Mantenimiento',
    subtitle: 'Tu piscina siempre perfecta',
    description: 'Servicio regular de limpieza y mantenimiento para que solo te preocupes de nadar.',
    features: ['Limpieza periódica', 'Control de pH', 'Revisión equipos', 'Informes mensuales'],
    image: 'https://images.unsplash.com/photo-1559732662-057ea8cffb06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxwb29sJTIwY2xlYW5pbmclMjBuZXQlMjBibHVlJTIwd2F0ZXJ8ZW58MHx8fHwxNzczMzE0OTAxfDA&ixlib=rb-4.1.0&q=85',
    color: 'from-cyan-500 to-sky-600',
    bgColor: 'bg-cyan-50',
  },
  {
    id: 'reparaciones',
    icon: Wrench,
    title: 'Reparaciones',
    subtitle: 'Soluciones rápidas',
    description: 'Reparamos fugas, grietas, bombas y cualquier avería con diagnóstico preciso.',
    features: ['Detección fugas', 'Reparación grietas', 'Bombas y motores', 'Urgencias'],
    image: 'https://images.unsplash.com/photo-1638024159638-b2e33e686489?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxwb29sJTIwdW5kZXJ3YXRlciUyMGJsdWUlMjBsaWdodCUyMGNhdXN0aWNzfGVufDB8fHx8MTc3MzMxODgyOHww&ixlib=rb-4.1.0&q=85',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-50',
  },
  {
    id: 'tratamiento',
    icon: Droplets,
    title: 'Tratamiento de Aguas',
    subtitle: 'Agua cristalina',
    description: 'Análisis profesional y tratamientos avanzados con las últimas tecnologías.',
    features: ['Cloración salina', 'Sistemas UV', 'Tratamiento ozono', 'Control pH'],
    image: 'https://images.unsplash.com/photo-1637605938651-24029b90fab9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxwb29sJTIwdW5kZXJ3YXRlciUyMGJsdWUlMjBsaWdodCUyMGNhdXN0aWNzfGVufDB8fHx8MTc3MzMxODgyOHww&ixlib=rb-4.1.0&q=85',
    color: 'from-teal-500 to-cyan-600',
    bgColor: 'bg-teal-50',
  },
  {
    id: 'climatizacion',
    icon: ThermometerSun,
    title: 'Climatización',
    subtitle: 'Disfruta todo el año',
    description: 'Sistemas de climatización eficientes que cuidan tu bolsillo y el planeta.',
    features: ['Bombas de calor', 'Paneles solares', 'Cubiertas térmicas', 'Aerotermia'],
    image: 'https://images.unsplash.com/photo-1770387200416-d499c2af0080?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBzdW5zZXQlMjBtZWRpdGVycmFuZWFuJTIwdmlsbGElMjBvY2VhbiUyMHZpZXd8ZW58MHx8fHwxNzczMzE4ODI3fDA&ixlib=rb-4.1.0&q=85',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-50',
  },
];

// 3D Service Detail Component
const ServiceDetail3D = ({ service, index }) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const Icon = service.icon;
  const isEven = index % 2 === 0;

  // 3D transforms
  const imageRotateY = useTransform(scrollYProgress, [0, 0.5, 1], [isEven ? -20 : 20, 0, isEven ? 10 : -10]);
  const imageRotateX = useTransform(scrollYProgress, [0, 0.5, 1], [15, 0, -10]);
  const imageY = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1.05, 0.95]);
  
  const contentY = useTransform(scrollYProgress, [0, 0.5, 1], [60, 0, -40]);
  const contentX = useTransform(scrollYProgress, [0, 0.4, 0.6, 1], [isEven ? 40 : -40, 0, 0, isEven ? -20 : 20]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0.5]);

  const springConfig = { stiffness: 80, damping: 20 };
  const imageYSpring = useSpring(imageY, springConfig);
  const contentYSpring = useSpring(contentY, springConfig);

  return (
    <motion.div
      ref={sectionRef}
      id={service.id}
      className={`py-12 sm:py-16 lg:py-24 ${index !== 0 ? 'border-t border-slate-100' : ''}`}
      data-testid={`service-detail-${service.id}`}
    >
      <div className={`grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
        {/* Image with 3D effect */}
        <motion.div
          style={{ 
            rotateY: imageRotateY, 
            rotateX: imageRotateX,
            y: imageYSpring, 
            scale: imageScale,
            transformStyle: 'preserve-3d',
            perspective: 1000,
          }}
          className={`relative ${!isEven ? 'lg:order-2' : ''}`}
        >
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
            <motion.img 
              src={service.image}
              alt={service.title}
              className="w-full h-[250px] sm:h-[350px] lg:h-[400px] object-cover"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.5 }}
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-20`} />
          </div>
          
          {/* 3D Floating badge */}
          <motion.div 
            className={`absolute -bottom-4 sm:-bottom-6 lg:-bottom-8 ${isEven ? '-right-2 sm:-right-4 lg:-right-8' : '-left-2 sm:-left-4 lg:-left-8'} ${service.bgColor} p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl shadow-xl`}
            style={{
              y: useTransform(scrollYProgress, [0, 1], [30, -30]),
              rotateZ: useTransform(scrollYProgress, [0, 1], [-5, 5]),
            }}
          >
            <div className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center`}>
              <Icon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
            </div>
          </motion.div>
        </motion.div>

        {/* Content */}
        <motion.div
          style={{ 
            y: contentYSpring, 
            x: contentX,
            opacity: contentOpacity,
          }}
          className={`${!isEven ? 'lg:order-1' : ''} px-2 sm:px-0`}
        >
          <motion.span 
            className={`inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 lg:mb-6 ${service.bgColor} text-slate-700`}
          >
            <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
            {service.subtitle}
          </motion.span>

          <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-slate-900 mb-3 sm:mb-4 lg:mb-6 tracking-tight">
            {service.title}
          </h2>

          <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 lg:mb-10 leading-relaxed">
            {service.description}
          </p>

          <ul className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4 mb-6 sm:mb-8 lg:mb-10">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="flex items-center gap-2 sm:gap-3"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-sky-500 flex-shrink-0" />
                <span className="text-slate-700 text-sm sm:text-base">{feature}</span>
              </motion.li>
            ))}
          </ul>

          <Link
            to="/contacto"
            className={`inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r ${service.color} text-white px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full font-semibold text-sm sm:text-base transition-all hover:scale-105 shadow-lg`}
          >
            Solicitar info
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
};

export const ServicesPage = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const progress = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const heroY = useTransform(scrollYProgress, [0, 0.2], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  return (
    <main ref={containerRef} className="pt-16 sm:pt-20 relative" data-testid="services-page">
      {/* Progress bar */}
      <motion.div className="fixed top-16 sm:top-20 left-0 right-0 h-1 bg-slate-100 z-40">
        <motion.div 
          className="h-full bg-gradient-to-r from-sky-500 to-blue-600"
          style={{ width: progress }}
        />
      </motion.div>

      {/* Hero with background */}
      <section className="relative py-16 sm:py-20 lg:py-24 overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroY }}
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_BG})` }}
          />
          <div className="absolute inset-0 bg-slate-900/70" />
        </motion.div>

        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-sky-300 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6 border border-white/20"
          >
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
            Servicios profesionales
          </motion.span>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white mb-4 sm:mb-6 tracking-tight px-4"
          >
            Soluciones para{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-400">
              tu piscina
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto px-4"
          >
            Desde la construcción hasta el mantenimiento diario.
          </motion.p>

          {/* Quick links */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-6 sm:mt-8 px-4"
          >
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <a
                  key={service.id}
                  href={`#${service.id}`}
                  className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all"
                >
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{service.title}</span>
                  <span className="sm:hidden">{service.title.split(' ')[0]}</span>
                </a>
              );
            })}
          </motion.div>
        </motion.div>
      </section>

      {/* Services */}
      <section className="bg-white max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {services.map((service, index) => (
          <ServiceDetail3D key={service.id} service={service} index={index} />
        ))}
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 lg:py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-sky-500/10"
              style={{
                width: 20 + Math.random() * 50,
                height: 20 + Math.random() * 50,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{ y: [0, -20, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 4 + Math.random() * 2, repeat: Infinity, delay: i * 0.3 }}
            />
          ))}
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-manrope font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white mb-4 sm:mb-6">
            ¿No encuentras lo que buscas?
          </h2>
          <p className="text-base sm:text-lg text-slate-400 mb-6 sm:mb-8 lg:mb-10">
            Contáctanos y seguro que podemos ayudarte.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
            <Link
              to="/contacto"
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-400 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-full font-bold text-sm sm:text-base transition-all hover:scale-105 shadow-xl text-center"
            >
              Contactar
            </Link>
            <a
              href="tel:+34634060878"
              className="flex items-center justify-center gap-2 text-sky-400 hover:text-sky-300 font-semibold text-sm sm:text-base"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              +34 634 06 08 78
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
